/**
 * B549 leg (2) — the GROUP BOOKING panel (444:2) and the GROUP INQUIRY
 * form (446:2), drawn to the sidecars in docs/design/skin-v2/pages/spec/
 * (redline-embed-group-interrupt.json / redline-embed-group-inquiry.json).
 * Every element a sidecar names carries its `data-redline` hook; the
 * geometry and registers live in styles.css on the same names.
 *
 * The panel REPLACES the tickets step's calendar column — never a toast,
 * never a modal, never an inline error (the frame's own note). The form
 * is a step of the sheet: trail `1 Tickets · 2 About your group · 3 Sent`,
 * `No payment now` in the foot where the total normally stands — nothing
 * on this step may ask for payment details.
 */
import type { ReactElement } from "react";
import { Button, Input, SegmentedControl, Select, sizedPhotoUrl } from "@transact/ui";
import { Notice } from "./screens.js";
import type { GroupInquiryDraft, GroupInquiryKind } from "../lib/groupBooking.js";
import { deskRepliesLead, deskSendsWords, groupExperienceLine, groupNoteText, groupPanelBlurb, groupPanelTitle, inquiryFieldErrors, inquiryGateReason, type CapMet, type InquiryField } from "../lib/groupBooking.js";

/* ------------------------------------------------------- the panel (444:2) */

export function GroupBookingPanel(props: {
  readonly threshold: number;
  readonly businessName?: string | undefined;
  /** B580 — the type cap the party passed, when that (not the threshold) made it a group. */
  readonly cap?: CapMet | undefined;
}): ReactElement {
  return (
    <div className="group-panel" aria-label="Group booking">
      <p className="group-panel-caption" data-redline="embed-group-interrupt-panel-caption">
        GROUP BOOKING
      </p>
      <h2 className="group-panel-title" data-redline="embed-group-interrupt-panel-title">
        {groupPanelTitle(props.threshold)}
      </h2>
      <p className="group-panel-blurb" data-redline="embed-group-interrupt-panel-blurb">
        {groupPanelBlurb(props.threshold, props.businessName)}
      </p>
      <ul className="group-perks">
        <li className="group-perk" data-redline="embed-group-interrupt-perk-1">
          <p className="group-perk-title" data-redline="embed-group-interrupt-perk-1-title">
            GROUP PRICING
          </p>
          <p className="group-perk-line" data-redline="embed-group-interrupt-perk-1-line">
            The desk quotes the group rate — usually less per seat than the public price
          </p>
        </li>
        <li className="group-perk" data-redline="embed-group-interrupt-perk-2">
          <p className="group-perk-title" data-redline="embed-group-interrupt-perk-2-title">
            ONE INVOICE
          </p>
          <p className="group-perk-line" data-redline="embed-group-interrupt-perk-2-line">
            Pay by invoice — a deposit now, the balance later, no card needed today
          </p>
        </li>
        <li className="group-perk" data-redline="embed-group-interrupt-perk-3">
          <p className="group-perk-title" data-redline="embed-group-interrupt-perk-3-title">
            ONE TICKET
          </p>
          <p className="group-perk-line" data-redline="embed-group-interrupt-perk-3-line">
            A single pass for the party, or per-seat tickets if you prefer
          </p>
        </li>
      </ul>
      <div className="group-note" data-redline="embed-group-interrupt-note">
        <p className="group-note-text" data-redline="embed-group-interrupt-note-text">
          {groupNoteText(props.threshold, props.cap)}
        </p>
      </div>
    </div>
  );
}

/* -------------------------------------------------------- the form (446:2) */

const KIND_OPTIONS = [
  { value: "school", label: "SCHOOL", redline: "embed-group-inquiry-kind-school" },
  { value: "corporate", label: "CORPORATE", redline: "embed-group-inquiry-kind-corporate" },
  { value: "tour", label: "TOUR", redline: "embed-group-inquiry-kind-tour" },
  { value: "other", label: "OTHER", redline: "embed-group-inquiry-kind-other" },
] as const;

/** B683 (R14) — the inquiry's form; SEND INQUIRY, drawn in the sheet foot / sticky bar outside it, submits it by this id. */
export const GROUP_INQUIRY_FORM_ID = "group-inquiry-form";

export function GroupInquiryForm(props: {
  readonly draft: GroupInquiryDraft;
  readonly onChange: (draft: GroupInquiryDraft) => void;
  /** The days the product actually sells, for PREFERRED DATE & TIME. */
  readonly dayOptions: readonly { readonly value: string; readonly label: string }[];
  readonly threshold: number;
  /** The card the buyer was composing: name, photo, public from-price. */
  readonly experience?: { readonly name: string; readonly photoUrl?: string | undefined; readonly publicFrom?: string | undefined } | undefined;
  readonly error?: string | undefined;
  readonly errorReference?: string | undefined;
  /** B549 (9): the first name the desk signs with (the channel's config); absent = the desk. */
  readonly deskName?: string | undefined;
  /** B589 (R18) — a refused send pinned at the field it was about; the buyer's own edit clears it. */
  readonly refused?: Partial<Record<InquiryField, string>> | undefined;
}): ReactElement {
  const d = props.draft;
  const set = (patch: Partial<GroupInquiryDraft>): void => props.onChange({ ...d, ...patch });
  /* B589 (R19) — each typed value judged for what it says, at its own field;
     a refusal the send brought back outranks nothing the buyer can see. */
  const errors = { ...props.refused, ...inquiryFieldErrors(d) };
  const gate = inquiryGateSentence(d);
  return (
    <div className="group-inquiry">
      <form id={GROUP_INQUIRY_FORM_ID} className="group-inquiry-form" aria-label="About your group" onSubmit={(e) => e.preventDefault()}>
        <p className="group-panel-caption" data-redline="embed-group-inquiry-form-caption">
          ABOUT YOUR GROUP
        </p>
        <h2 className="group-panel-title" data-redline="embed-group-inquiry-form-title">
          Tell us who’s coming
        </h2>
        {props.error !== undefined ? (
          <Notice kind="error" reference={props.errorReference}>
            {props.error}
          </Notice>
        ) : null}
        <div className="group-inquiry-row">
          <Input
            id="gi-organisation"
            label="ORGANISATION"
            value={d.organisation}
            onChange={(organisation) => set({ organisation })}
            redline="embed-group-inquiry-organisation"
            placeholder="Lincoln Elementary"
            error={errors.organisation}
          />
          <Input
            id="gi-how-many"
            label="HOW MANY"
            value={d.headcount}
            onChange={(headcount) => set({ headcount })}
            inputMode="numeric"
            redline="embed-group-inquiry-how-many"
            width="sm"
            placeholder="25"
            error={errors.headcount}
          />
        </div>
        <SegmentedControl
          legend="WHAT KIND OF GROUP"
          legendVisible
          name="gi-kind"
          options={KIND_OPTIONS}
          value={d.kind}
          onChange={(kind) => set({ kind: kind as GroupInquiryKind })}
          redline="embed-group-inquiry-kind"
        />
        <div className="group-inquiry-row">
          <Input id="gi-name" label="YOUR NAME" value={d.contactName} onChange={(contactName) => set({ contactName })} redline="embed-group-inquiry-name" placeholder="Dana Field" error={errors.contactName} />
          <Input id="gi-email" label="EMAIL" type="email" value={d.contactEmail} onChange={(contactEmail) => set({ contactEmail })} redline="embed-group-inquiry-email" placeholder="dana@school.org" error={errors.contactEmail} />
        </div>
        <div className="group-inquiry-row">
          <Input id="gi-phone" label="PHONE (OPTIONAL)" type="tel" value={d.contactPhone} onChange={(contactPhone) => set({ contactPhone })} redline="embed-group-inquiry-phone" placeholder="(555) 010-0199" error={errors.contactPhone} />
          <Select
            id="gi-date"
            label="PREFERRED DATE & TIME"
            options={props.dayOptions}
            value={d.preferredDate}
            onChange={(preferredDate) => set({ preferredDate })}
            placeholder="Any day — we’ll work it out"
            redline="embed-group-inquiry-date"
          />
        </div>
        <Input id="gi-notes" label="ANYTHING ELSE?" value={d.notes} onChange={(notes) => set({ notes })} redline="embed-group-inquiry-notes" placeholder="Ages, access needs, a lunch stop…" error={errors.notes} />
        {/* B589 (R15) — SEND's shut reason, VISIBLE under the fields it is
            about; the door in the foot points here (describedBy gi-gate). */}
        {gate !== undefined ? (
          <p className="t-footnote group-inquiry-gate" id="gi-gate" role="status">
            {gate}
          </p>
        ) : null}
      </form>

      <aside className="group-inquiry-side" aria-label="What happens next">
        <p className="group-panel-caption" data-redline="embed-group-inquiry-next-caption">
          WHAT HAPPENS NEXT
        </p>
        <ol className="group-steps">
          <li className="group-step" data-redline="embed-group-inquiry-step-1">
            <span className="group-step-n" data-redline="embed-group-inquiry-step-1-n" aria-hidden="true">
              1
            </span>
            <div>
              <p className="group-step-title" data-redline="embed-group-inquiry-step-1-title">
                {deskSendsWords(props.deskName)}
              </p>
              <p className="group-step-line" data-redline="embed-group-inquiry-step-1-line">
                Group price list applied · seats held for 14 days while you decide
              </p>
            </div>
          </li>
          <li className="group-step" data-redline="embed-group-inquiry-step-2">
            <span className="group-step-n" data-redline="embed-group-inquiry-step-2-n" aria-hidden="true">
              2
            </span>
            <div>
              <p className="group-step-title" data-redline="embed-group-inquiry-step-2-title">
                You accept online
              </p>
              <p className="group-step-line" data-redline="embed-group-inquiry-step-2-line">
                A deposit or a purchase order confirms the booking — the seats become yours
              </p>
            </div>
          </li>
          <li className="group-step" data-redline="embed-group-inquiry-step-3">
            <span className="group-step-n" data-redline="embed-group-inquiry-step-3-n" aria-hidden="true">
              3
            </span>
            <div>
              <p className="group-step-title" data-redline="embed-group-inquiry-step-3-title">
                Balance &amp; tickets
              </p>
              <p className="group-step-line" data-redline="embed-group-inquiry-step-3-line">
                Settle the balance by invoice before the visit — the tickets arrive by email
              </p>
            </div>
          </li>
        </ol>
        {props.experience !== undefined ? (
          <>
            <p className="group-panel-caption" data-redline="embed-group-inquiry-experience-caption">
              YOUR EXPERIENCE
            </p>
            <div className="group-experience" data-redline="embed-group-inquiry-experience">
              {/* B626 — the asset host serves photos SIZED only (B521: the original is never
                  served), so this card asks for its own size like every other surface; a raw
                  url drew the browser's broken-image glyph (Pathik 2026-10-01, Phoenix). */}
              {props.experience.photoUrl !== undefined ? (
                <img className="group-experience-photo" data-redline="embed-group-inquiry-experience-photo" src={sizedPhotoUrl(props.experience.photoUrl, 192)} alt="" />
              ) : (
                <span className="group-experience-photo" data-redline="embed-group-inquiry-experience-photo" aria-hidden="true" />
              )}
              <div>
                <p className="group-experience-title" data-redline="embed-group-inquiry-experience-title">
                  {props.experience.name}
                </p>
                <p className="group-experience-line" data-redline="embed-group-inquiry-experience-line">
                  {groupExperienceLine(props.threshold, props.experience.publicFrom)}
                </p>
              </div>
            </div>
          </>
        ) : null}
      </aside>
    </div>
  );
}

/* ------------------------------------------------------------- sent (step 3) */

export function GroupInquirySent(props: { readonly email: string; readonly onClose?: (() => void) | undefined; readonly deskName?: string | undefined }): ReactElement {
  return (
    <div className="group-sent" data-redline="embed-group-inquiry-sent">
      <p className="group-panel-caption">GROUP BOOKING</p>
      <h2 className="group-panel-title">Your inquiry is on its way</h2>
      <p className="group-panel-blurb">
        {deskRepliesLead(props.deskName)} to <strong>{props.email}</strong> within one business day with a quote. Nothing is booked and nothing is charged until you accept it.
      </p>
      {props.onClose !== undefined ? (
        <Button variant="neutral" onClick={props.onClose}>
          Done
        </Button>
      ) : null}
    </div>
  );
}

/* --------------------------------------------------- the form's foot facts */

/** The SEND door's gate sentence, or undefined when armed (R14: one statement — B589: what to add AND what to fix). */
export function inquiryGateSentence(d: GroupInquiryDraft): string | undefined {
  return inquiryGateReason(d);
}
